import { Box, Stack } from '@mui/material';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Overlay } from '../../base/overlay/Overlay';
import { TutorialContext } from '../../base/tutorial/tutorialTracker/TutorialTracker';

interface PopupOnOverlay {
    key?: string,
    expandedChildren(setViewExpandedChildren: (viewing: boolean) => void): any
    children: any
    blockExpand?: boolean
}

export const PopupOnOverlayComponent = (props: PopupOnOverlay) => {

    // --- 1. Create a stable, unique ID that persists across re-renders ---
    const idRef = useRef(`popup-${Math.random().toString(36).substring(2, 9)}`);

    const [viewExpandedChildren, setViewExpandedChildren] = useState(false)
    const tutorialContext = useContext(TutorialContext)
    const { registerPopup, unregisterPopup } = usePopupOnOverlayContext()

    // --- 2. Register on mount and unregister on unmount ---
    useEffect(() => {
        // When viewExpandedChildren becomes true, we register the popup
        if (viewExpandedChildren) {
            registerPopup(idRef.current);
        }
        else {
            return
        }
        // The cleanup function will be called when viewExpandedChildren changes from true to false,
        // or when the component unmounts.
        const id = idRef.current; //Not really needed as idRef doesn't change, but eslint complains and this makes it more robust against unwitting changes
        return () => {
            unregisterPopup(id);
        };
        // This effect depends on whether the popup is open and the registration functions
    }, [viewExpandedChildren, registerPopup, unregisterPopup]);

    //Only exit out if there are no tutorials showing
    const overlayOnclick = () => {
        if (!tutorialContext.currentTutorial) {
            setViewExpandedChildren(false)
        }
    }

    return <>{viewExpandedChildren ? <Overlay onclick={overlayOnclick} usePortal>
        <Stack justifyContent={'center'} justifyItems={'center'} alignItems={'center'}>
            {props.expandedChildren(setViewExpandedChildren)}
        </Stack>
    </Overlay> : ''}
        <Box onClick={() => {
            if (!props.blockExpand) {
                setViewExpandedChildren(true)
            }
        }}>
            {props.children}
        </Box>
    </>

}


export const PopupOnOverlayContext = React.createContext({
    registerPopup: (id: string) => { },
    unregisterPopup: (id: string) => { },
    isActivePopup: false
})


export const PopupOnOverlayContextProvider = PopupOnOverlayContext.Provider;

export const usePopupOnOverlayContext = () => React.useContext(PopupOnOverlayContext);

//this component exists just so that other components may check if a pop up is already rendered
//It might get used in decisions on whether to pause certain transitions or to avoid displaying a tutorial
export const PopupOnOverlayContextComponent = (props: { children: any }) => {
    const [popups, setPopups] = React.useState<string[]>([])

    const registerPopup = useMemo(() => {
        return (id: string) => {
            setPopups(current => {
                return [...current, id]
            })
        }
    }, [setPopups])

    const unregisterPopup = useMemo(() => {
        return (id: string) => {
            setPopups(current => {
                return current.filter(popup => popup !== id)
            })
        }
    }, [setPopups])


    return <PopupOnOverlayContextProvider value={{
        registerPopup,
        unregisterPopup,
        isActivePopup: popups.length > 0
    }}>
        {props.children}
    </PopupOnOverlayContextProvider>

}

